import '../css/Companies.css';

import {
    LinearProgress,
    MenuItem,
    MenuList,
    Typography,
} from '@material-ui/core';
import { deleteCompanies, migrateCompanies } from './Services';
import { isEqual, sortBy } from 'lodash';

import Access from 'ui/Access';
import Avatars from 'ui/Avatars';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';
import { Link } from 'react-router-dom';
import { Permissions } from 'utils/permissions';
import PropTypes from 'prop-types';
import React from 'react';
import StatusIcon from 'ui/StatusIcon';
import VirtualizedTable from '../ui/mtablevirtualized/VirtualizedTable';
import WhiteBack from './WhiteBack';
import { CONNECTORS as connectorsConfig } from './company/connectors/Connectors';
import { flowRight } from 'lodash/fp';
import formatters from '../formatters';
import integrationsConfigs from './integrations/types/configs';
import { isPaymentMethodSetup } from 'utils/user';
import { loadServices } from './integrations/services';
import { tableStyle } from '../ui/mtable/styles';
import { withAppContext } from './AppContext';
import { withCompanies } from './hooks/withCompanies';
import { withStyles } from '@material-ui/core/styles';

const DELETE_COMPANIES = 'deleteCompanies';
const MIGRATE_COMPANY = 'migrateCompany';
const MIGRATE_COMPANY_RESULT = 'migrateCompanyResult';
const MIGRATE_COMPANY_ERROR = 'migrateCompanyError';
const COMPANY_CONTAINS_USERS = 'companyContainsUsers';

class Companies extends React.Component {
    constructor(props) {
        super(props);
        this.childRef = React.createRef();
    }

    state = {
        companies: [],
        selectedCompanies: [],
        rootCompany: null,
        loading: true,
        validating: true,
        loadingServices: true,
        paymentInfo: false,
        integrationActions: [],
        currentAction: null,
        migrationResult: null,
        migrationError: null,
        migrating: false,
    };

    componentDidMount() {
        const { app, updateBreadcrumbs } = this.props;

        if (app.canChange(Access.INTEGRATIONS)) {
            this.getIntegrationActions();
        } else {
            this.setState({ loadingServices: false });
        }
        updateBreadcrumbs(['Companies']);
    }

    componentDidUpdate(prevProps) {
        const { data, error, isLoading, isValidating } = this.props;
        const isEqualsList = isEqual(
            sortBy(this.state.companies, 'uuid'),
            sortBy(data, 'uuid')
        );

        if (
            this.state.loading !== isLoading ||
            !isEqualsList ||
            this.state.validating !== isValidating
        ) {
            const updates = {};

            if (!isLoading && (this.state.loading || !isEqualsList)) {
                updates.companies = data;
                updates.rootCompany = data.find(company => !company.parent);
                updates.loading = false;
                updates.selectedCompanies = [];
            }

            if (!isValidating && this.state.validating) {
                updates.validating = false;
            }

            if (Object.keys(updates).length) {
                this.setState(updates);
            }
        }
    }

    resetSelectedUsers = () => {
        if (this.childRef.current) {
            this.childRef.current.resetSelectedRows();
        }
    };

    getIntegrationActions = async () => {
        const services = await loadServices();
        const enabledIntegrations = integrationsConfigs
            .map(config => {
                const service = services.find(
                    service => service.identifier === config.name
                );
                return { ...config, service };
            })
            .filter(
                config =>
                    config.enabled &&
                    !config.dummy &&
                    config.service &&
                    config.service.extra &&
                    config.service.extra.integration_status
            );
        const integrationActions = [];
        for (const config of enabledIntegrations) {
            const mappingLookups = await config.serviceProvider.productsLookups(
                config.service
            );
            const mappings = await config.serviceProvider.products(
                config.service
            );
            if (mappingLookups.length === mappings.length) {
                integrationActions.push({
                    name: config.name,
                    label: config.label,
                });
            }
        }
        this.setState({
            loadingServices: false,
            integrationActions,
        });
    };

    getAddActions = user => () => {
        let button = null;
        const { rootCompany, integrationActions, loadingServices } = this.state;
        if (!isPaymentMethodSetup(user)) {
            button = (
                <>
                    <MenuItem onClick={this.showPaymentInfo}>
                        Add company
                    </MenuItem>
                    {integrationActions.map(item => (
                        <MenuItem onClick={this.showPaymentInfo}>
                            Add company from {item.label}
                        </MenuItem>
                    ))}
                </>
            );
        } else if (rootCompany) {
            const integrationURL = `/companies/create_from/${rootCompany.uuid}`;
            button = (
                <>
                    <MenuItem
                        component={Link}
                        to={`/companies/create/${rootCompany.uuid}`}
                    >
                        Add company
                    </MenuItem>
                    {integrationActions.map(item => (
                        <MenuItem
                            component={Link}
                            to={`${integrationURL}/${item.name}`}
                        >
                            Add company from {item.label}
                        </MenuItem>
                    ))}
                </>
            );
        }
        if (button) {
            return (
                <MenuList>
                    {button}
                    {loadingServices && (
                        <LinearProgress style={{ bottom: -8 }} />
                    )}
                </MenuList>
            );
        }
        return null;
    };

    getPlainCompanies = companies => {
        return companies.reduce((acc, val) => {
            acc.push(val);

            if (val.children) {
                acc = acc.concat(this.getPlainCompanies(val.children));
            }

            return acc;
        }, []);
    };

    showPaymentInfo = () => {
        this.setState({ paymentInfo: true });
    };

    closePaymentInfo = () => {
        this.setState({ paymentInfo: false });
    };

    handleAction = currentAction => selectedCompanies => {
        this.setState({
            currentAction,
            selectedCompanies,
        });
    };

    getActions = () => {
        return [
            {
                key: DELETE_COMPANIES,
                name: 'Delete company',
                action: this.handleAction(DELETE_COMPANIES),
            },
            {
                key: MIGRATE_COMPANY,
                name: 'Migrate company',
                action: this.handleAction(MIGRATE_COMPANY),
            },
        ];
    };

    getAvailableActions = (selectedCompanies, actions) =>
        actions.filter(action => {
            if (
                action.key === DELETE_COMPANIES ||
                action.key === MIGRATE_COMPANY
            ) {
                const { app } = this.props;
                const userOrganization = app.currentUser.organizations[0];
                const isUserCanDeleteCompany = app.currentUser.canChange(
                    Permissions.COMPANIES_DELETE_COMPANY
                );
                const allowedCompanyTypes =
                    userOrganization.type === 'msp' ||
                    userOrganization.type === 'reseller_msp';
                const baseCondition =
                    isUserCanDeleteCompany &&
                    selectedCompanies.every(
                        company =>
                            company.children.length === 0 &&
                            (userOrganization.is_super_pb_partner ||
                                (company.parent === userOrganization.uuid &&
                                    allowedCompanyTypes &&
                                    company.type === 'Customer'))
                    );
                if (action.key === MIGRATE_COMPANY) {
                    return (
                        app.currentUser.canChange(
                            Permissions.COMPANIES_MIGRATE
                        ) &&
                        baseCondition &&
                        selectedCompanies.some(c => !c.pb6_enabled)
                    );
                }
                return baseCondition;
            } else {
                return true;
            }
        });

    getActionMenuItems = selectedCompanies => {
        const actions = this.getActions();
        const availableActions = this.getAvailableActions(
            selectedCompanies,
            actions
        );

        return availableActions.length > 0
            ? availableActions.map(action => ({
                  key: action.key,
                  name: action.name,
                  onClick: dropdownCloseCallback => {
                      action.action(selectedCompanies);
                      dropdownCloseCallback();
                  },
              }))
            : [
                  {
                      key: 'no',
                      name: 'No common action',
                      onClick: dropdownCloseCallback => {
                          dropdownCloseCallback();
                      },
                  },
              ];
    };

    handleSelectionChange = selectedCompanies => {
        this.setState({ selectedCompanies });
    };

    renderDialog = () => {
        const {
            currentAction,
            selectedCompanies,
            migrationResult,
            migrationError,
            migrating,
        } = this.state;
        const migratableCompanies = selectedCompanies.filter(
            c => !c.pb6_enabled
        );
        const dialog = {
            [DELETE_COMPANIES]: {
                title: 'Delete Company',
                subtitle1: (
                    <>
                        Deleting a company is PERMANENT and cannot be undone.
                        Deleting a company will also:
                        <ul style={{ marginTop: 5, marginBottom: 0 }}>
                            <li>Delete all user accounts</li>
                            <li>
                                Delete all saved passwords and notes for all
                                users
                            </li>
                            <li>Cancel all shares and emergency access</li>
                        </ul>
                    </>
                ),
                subtitle2: 'Are you sure you want to delete this company:',
                confirmByTyping: 'yes',
                okButtonName: 'Delete Company',
            },
            [MIGRATE_COMPANY]: {
                title: 'Migrate Company',
                subtitle1:
                    'Enabling the migration to web-app will make all your company accounts eligible for the migration process. During the migration, you will still have access to the old desktop version; however, once the migration is complete, reverting to the previous version will no longer be possible. You will still be able to open and use the old desktop, but any actions performed there will not be persisted. All synchronization and backup operations on the old version will be permanently stopped and transitioned to the new one.',
                subtitle2: 'Do you want to proceed with the migration?',
                link: {
                    url:
                        'https://support.cyberfox.com/115000433968-Password-Boss-Basics/migration',
                    name: 'Learn how migration works',
                },
                okButtonName: 'Confirm',
            },
            [MIGRATE_COMPANY_RESULT]: {
                title: 'Migration Complete',
                subtitle1: 'The migration process has been completed.',
                data: (migrationResult || []).map(result => {
                    if (result.total_migrated === 0) {
                        return `${result.companyName} — nothing to migrate`;
                    }
                    return `${result.companyName} — ${result.total_migrated} account/s migrated`;
                }),
                okButtonName: 'OK',
            },
            [MIGRATE_COMPANY_ERROR]: {
                title: 'Migration Failed',
                warning:
                    migrationError || 'Migration failed. Please try again.',
                okButtonName: 'OK',
            },
            [COMPANY_CONTAINS_USERS]: {
                title: 'Deleting A Company',
                warning:
                    'Before deleting a company you need to delete all of the users in the company except for the primary contact user.  The primary contact user is identified with an orange star next to their name on the Users tab for the company.  Once all users except the primary contact user have been deleted you can delete the company.',
                okButtonName: 'OK',
            },
        };

        const callbacks = {
            [DELETE_COMPANIES]: {
                callback: async () => {
                    const uuids = selectedCompanies.map(
                        company => company.uuid
                    );
                    try {
                        this.setState({ loading: true });
                        await deleteCompanies(
                            this.props.app.currentUser.uuid,
                            uuids
                        );
                        this.setState({
                            currentAction: null,
                            selectedCompanies: [],
                        });
                    } catch (e) {
                        if (e.id === 657) {
                            this.setState({
                                currentAction: COMPANY_CONTAINS_USERS,
                                selectedCompanies: [],
                            });
                        }
                        this.setState({ loading: false });
                    }
                },
            },
            [MIGRATE_COMPANY]: {
                callback: async () => {
                    const uuids = migratableCompanies.map(c => c.uuid);
                    try {
                        this.setState({ loading: true, migrating: true });
                        const results = await migrateCompanies(uuids);
                        const enrichedResults = (results || []).map(result => {
                            const company = migratableCompanies.find(
                                c => c.uuid === result.organization_uuid
                            );
                            return {
                                ...result,
                                companyName: company
                                    ? company.name
                                    : result.organization_uuid,
                            };
                        });
                        this.props.reloadCompanies();
                        this.setState({
                            currentAction: MIGRATE_COMPANY_RESULT,
                            migrationResult: enrichedResults,
                            selectedCompanies: [],
                            loading: false,
                            migrating: false,
                        });
                    } catch (e) {
                        this.setState({
                            currentAction: MIGRATE_COMPANY_ERROR,
                            migrationError:
                                e.error ||
                                'Migration failed. Please try again.',
                            loading: false,
                            migrating: false,
                        });
                    }
                },
            },
            [MIGRATE_COMPANY_RESULT]: {
                callback: () => {
                    this.setState({
                        currentAction: null,
                        migrationResult: null,
                    });
                },
            },
            [MIGRATE_COMPANY_ERROR]: {
                callback: () => {
                    this.setState({
                        currentAction: null,
                        migrationError: null,
                    });
                },
            },
            [COMPANY_CONTAINS_USERS]: {
                callback: () => {
                    this.setState({
                        currentAction: null,
                        selectedCompanies: [],
                    });
                },
            },
        };

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    showCancelButton={
                        currentAction !== COMPANY_CONTAINS_USERS &&
                        currentAction !== MIGRATE_COMPANY_RESULT &&
                        currentAction !== MIGRATE_COMPANY_ERROR &&
                        !migrating
                    }
                    open={true}
                    data={(currentAction === MIGRATE_COMPANY
                        ? migratableCompanies
                        : selectedCompanies
                    ).map(company => company.name)}
                    {...dialog[currentAction]}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                    loading={
                        currentAction === MIGRATE_COMPANY ? migrating : false
                    }
                />
            );
        }
    };

    handleCloseDialog = () => () => {
        this.setState({ currentAction: null });
    };

    handleOkDialog = ({ callback, actionResultName }) => () => {
        this.resetSelectedUsers();
        const { selectedCompanies } = this.state;
        this.setState({ actionResultName }, () =>
            callback(selectedCompanies, Boolean(actionResultName))
        );
    };

    render() {
        const columns = [
            {
                accessorKey: 'uuid',
                header: 'Uuid',
                field: 'uuid',
                title: 'Uuid',
                hidden: true,
                notFilter: true,
                export: false,
            },
            {
                accessorKey: 'business_name',
                header: 'Name',
                Cell: ({ cell }) => (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'left',
                            alignItems: 'center',
                        }}
                    >
                        <Avatars.Small
                            src={cell.row.original.logo}
                            name={cell.row.original.business_name}
                            color={cell.row.original.color}
                        />
                        <Link
                            style={{
                                paddingLeft: '5px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                            to={`companies/${cell.row.original.uuid}`}
                        >
                            {cell.row.original.business_name}
                        </Link>
                    </div>
                ),
                field: 'business_name',
                title: 'Name',
                defaultSort: 'asc',
                customSort: (a, b) =>
                    a.business_name
                        .toLowerCase()
                        .localeCompare(b.business_name.toLowerCase()),
                cellStyle: {
                    minWidth: '30%',
                },
            },
            {
                accessorKey: 'name',
                header: 'Business profile name',
                field: 'name',
                title: 'Business profile name',
                cellStyle: {
                    width: '300px',
                },
            },
            {
                accessorKey: 'identifier',
                header: 'Account Id',
                size: 140,
                field: 'identifier',
                title: 'Account Id',
                cellStyle: {
                    width: '150px',
                },
                headerStyle: {
                    paddingRight: '0px',
                },
            },
            {
                accessorKey: 'type',
                header: 'Type',
                size: 140,
                field: 'type',
                title: 'Type',
                cellStyle: {
                    width: '120px',
                },
            },
            {
                accessorKey: 'subscription',
                header: 'Subscription',
                field: 'subscription',
                title: 'Subscription',
                size: 140,
                cellStyle: {
                    width: '120px',
                },
                headerStyle: {
                    paddingRight: '0px',
                },
            },
            {
                accessorKey: 'subscription_seats',
                header: 'Licenses',
                size: 110,
                field: 'subscription_seats',
                title: 'Licenses',
                cellStyle: {
                    width: '110px',
                },
                headerStyle: {
                    paddingRight: '0px',
                },
            },
            {
                accessorKey: 'users',
                header: 'Users',
                size: 110,
                field: 'users',
                title: 'Users',
                cellStyle: {
                    width: '80px',
                },
                headerStyle: {
                    paddingRight: '0px',
                },
            },
            {
                accessorKey: 'devices',
                header: 'Devices',
                size: 110,
                field: 'devices',
                title: 'Devices',
                cellStyle: {
                    width: '30px',
                },
                headerStyle: {
                    paddingRight: '0px',
                },
            },
            {
                accessorKey: 'policies',
                header: 'Policies',
                size: 110,
                field: 'policies',
                title: 'Policies',
                cellStyle: {
                    width: '80px',
                },
                headerStyle: {
                    paddingRight: '0px',
                },
            },
            {
                accessorKey: 'psa',
                header: 'PSA',
                size: 110,
                Cell: ({ cell }) => (
                    <PSAIcons
                        classes={this.props.classes}
                        integrations={cell.row.original.connectors}
                    />
                ),
                field: 'psa',
                title: 'PSA',
                cellStyle: {
                    width: '25px',
                    whiteSpace: 'nowrap',
                },
            },
            {
                accessorKey: 'dir',
                header: 'Dir',
                size: 110,
                Cell: ({ cell }) =>
                    cell.row.original.connectors.length > 0 ? (
                        <DirIcon
                            classes={this.props.classes}
                            connectors={cell.row.original.connectors}
                        />
                    ) : null,
                field: 'dir',
                title: 'Dir',
                cellStyle: {
                    width: '25px',
                    whiteSpace: 'nowrap',
                },
            },
            {
                accessorKey: 'pb6_enabled',
                header: 'PB6 Enabled',
                size: 140,
                Cell: ({ cell }) => (
                    <StatusIcon active={cell.row.original.pb6_enabled} />
                ),
                field: 'pb6_enabled',
                title: 'PB6 Enabled',
                filter: (value, filter) => value === filter.value,
                filterType: 'boolean',
                cellStyle: {
                    minWidth: '140px',
                },
            },
            {
                accessorKey: 'managed_by',
                header: 'Managed By',
                size: 140,
                // The value here is the parent organization's name, so this is
                // where "PB Partners" and friends surface.
                Cell: ({ cell }) => (
                    <span>
                        {formatters.managedByLabel(
                            cell.row.original.managed_by
                        )}
                    </span>
                ),
                exportLabel: val => formatters.managedByLabel(val),
                field: 'managed_by',
                title: 'Managed By',
                cellStyle: {
                    width: '170px',
                },
            },
        ];
        const {
            paymentInfo,
            companies,
            loading,
            selectedCompanies,
            currentAction,
        } = this.state;
        const { app, classes } = this.props;
        const selectedActions = this.getActionMenuItems(selectedCompanies);

        return (
            <div className={classes.tableContainer}>
                {currentAction && this.renderDialog()}
                {paymentInfo && (
                    <PaymentMethodInfo onClose={this.closePaymentInfo} />
                )}
                <WhiteBack />
                <VirtualizedTable
                    stateKey="Companies"
                    sortingKey="business_name"
                    columns={columns}
                    data={companies}
                    loading={this.state.loading}
                    validating={this.state.validating}
                    canChange={app.canChange(Access.COMPANIES_ADD_COMPANY)}
                    actionMenuItems={selectedActions}
                    onSelectionChange={this.handleSelectionChange}
                    addMenuItems={this.getAddActions(app.currentUser)}
                    searchPlaceholder="Find companies"
                    parentChildData={(row, rows) => {
                        const { rootCompany } = this.state;
                        return rows.find(
                            a =>
                                a.uuid === row.parent &&
                                a.uuid !== rootCompany.uuid
                        );
                    }}
                    options={{
                        clearCheckboxMargin: true,
                        selection: true,
                    }}
                    enableExpanding={true}
                    height={'160px'}
                    ref={this.childRef}
                />
            </div>
        );
    }
}

const Text = ({ children, ...props }) => (
    <Typography variant="body1" color="inherit" {...props}>
        {children}
    </Typography>
);

function PaymentMethodInfo({ onClose }) {
    const title = `Payment method`;
    const info = (
        <div>
            <Text>
                Please add payment details to your account to before adding
                customers. <br />
                Please go to&nbsp;
                <Text to={'/account/details/payment_method'} component={Link}>
                    Account / Details / Payment Method
                </Text>
                &nbsp;to add your payment details.
            </Text>
        </div>
    );
    return (
        <ConfirmationDialog
            open={true}
            title={title}
            subtitle1={info}
            onOk={onClose}
            showCancelButton={false}
        />
    );
}

const PSAIcons = ({ integrations, classes }) => {
    return (
        <>
            {integrationsConfigs
                .filter(integration => integrations.includes(integration.name))
                .map(integration => (
                    <img
                        className={classes.connectorIcon}
                        src={integration.icon}
                        alt={integration.label}
                    />
                ))}
        </>
    );
};

const DirIcon = ({ connectors, classes }) => {
    const connector = connectorsConfig.find(connector =>
        connectors.includes(connector.identifier)
    );
    return connector ? (
        <img
            className={classes.connectorIcon}
            src={connector.icon}
            alt={connector.shortName}
        />
    ) : null;
};

Companies.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default flowRight(
    withAppContext,
    withStyles(tableStyle),
    withCompanies
)(Companies);
